import NotificationSkeleton from '@/components/skeletons/NotificationSkeleton'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { getNotificationsByDocumentType } from '@/modules/Dashboard/services'
import { Documents } from '@/modules/Documents/types/reponse'
import { TypeDocument } from '@/modules/Documents/types/utils'
import { randomAvatarSrc } from '@/utils/function'
import { paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, Button, Typogaphy } from 'vnetwork-ui'
import AttachmentCard from './AttachmentCard'
import { TFunction } from 'i18next'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import EyeIcons from '@/icons/EyeIcons'

interface NotificationContentProps {
    notification: string
}
const buildQueryParams = (t: TFunction, type: string) => {
    if (type !== 'OTHER') {
        return btoa(
            JSON.stringify({
                document_type: [type],
            })
        )
    }
    return btoa(
        JSON.stringify({
            document_type: TypeDocument(t)
                ?.filter((item) => item.value !== 'OTHER')
                ?.map((item) => item.value),
        })
    )
}

const NotificationContent: React.FC<NotificationContentProps> = ({
    notification,
}) => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const { data: notifications, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.NOTIFICATIONS, notification],
            queryFn: () => getNotificationsByDocumentType(notification),
            enabled: !!notification,
        },
        {
            loadingComponent: <NotificationSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    const handleViewMore = () => {
        navigate(
            paths.documentList + '?filters=' + buildQueryParams(t, notification)
        )
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // Show all items with scroll, but still have view more for very long lists
    const displayedNotifications = notifications?.results || []
    const hasMoreItems = (displayedNotifications?.length || 0) > 5 // Show "view more" if more than 5 items

    return (
        <>
            <div className="xs:max-h-[300px] md:max-h-[520px] xs:min-h-[300px] md:min-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="space-y-3">
                    {displayedNotifications?.map(
                        (notification: Documents, index: number) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 rounded-lg"
                            >
                                <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0">
                                    <Avatar
                                        src={randomAvatarSrc(
                                            notification.employee_name
                                        )}
                                        className="!w-[34px] !h-[34px]"
                                    />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <Typogaphy className="font-medium text-sm text-gray-900 mb-1">
                                        {notification.employee_name}{' '}
                                        {t('sharing')} {notification.name_file}
                                    </Typogaphy>
                                    <TextEditorCustomize
                                        value={{
                                            text: notification.descriptions,
                                            attachments: [],
                                        }}
                                        readOnly={true}
                                        maxLine={3}
                                        className="text-[#B4B4B4]"
                                    />
                                    {notification?.versions?.[0]
                                        ?.attachment && (
                                        <div className="mt-2">
                                            <AttachmentCard
                                                attachment={
                                                    notification.versions[0]
                                                        .attachment
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></div>
                            </div>
                        )
                    )}

                    {(!notifications?.results ||
                        notifications?.results?.length === 0) && (
                        <div className="text-center py-8">
                            <Typogaphy className="text-gray-500 capitalize">
                                {t('notifications.no_data')?.toLowerCase()}{' '}
                                {notification}
                            </Typogaphy>
                        </div>
                    )}
                </div>
            </div>

            {/* View More Button - Outside scroll container */}
            {hasMoreItems && (
                <div className="pt-2 mt-3">
                    <Button
                        onClick={handleViewMore}
                        variant="outlined"
                        size="small"
                        startIcon={<EyeIcons />}
                        className="w-full text-center py-2 text-sm font-medium bg-secondary-10 text-primary"
                    >
                        {t('view_all')}
                    </Button>
                </div>
            )}
        </>
    )
}

export default NotificationContent
